import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Stepper, 
  Step, 
  StepLabel, 
  Paper, 
  Button, 
  Typography, 
  Box,
  Container,
  Card,
  CardContent
} from '@mui/material';
import { useRegistration } from '../../../context/RegistrationContext';
import PersonalInfoStep from './PersonalInfoStep';
import ProfessionalInfoStep from './ProfessionalInfoStep';
import AvailabilityStep from './AvailabilityStep';
import RegisterSuccess from './RegisterSuccess';
import { astrologerRegistrationService } from '../../../services/api';
import { ErrorMessage, LoadingSpinner } from '../../common';

const steps = ['Personal Information', 'Professional Details', 'Availability'];

const RegistrationForm = () => {
  const navigate = useNavigate();
  const { 
    token, email, formData, updateFormData, 
    currentStep, setCurrentStep, isValid 
  } = useRegistration();
  
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [registrationComplete, setRegistrationComplete] = useState(false);
  
  // Redirect if no token or email in context
  useEffect(() => {
    if (!token || !email) {
      navigate('/unauthorized');
    }
  }, [token, email, navigate]);
  
  const handleNext = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };
  
  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');
    
    try {
      // Combine all form data with token
      const registrationData = {
        token,
        email,
        ...formData.personal,
        ...formData.professional,
        ...formData.availability
      };
      
      await astrologerRegistrationService.registerAstrologer(registrationData);
      setRegistrationComplete(true);
    } catch (error) {
      if (error.response?.data?.message) {
        setError(error.response.data.message);
      } else {
        setError('An error occurred during registration. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };
  
  // Registration success view
  if (registrationComplete) {
    return <RegisterSuccess />;
  }
  
  return (
    <Container maxWidth="md">
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 4, textAlign: 'center' }}>
          Complete Your Astrologer Registration
        </Typography>
        
        <Card sx={{ boxShadow: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Stepper activeStep={currentStep} sx={{ mb: 4 }} alternativeLabel>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            
            {error && <ErrorMessage message={error} />}
            
            {submitting ? (
              <LoadingSpinner message="Processing your registration..." />
            ) : (
              <Box sx={{ minHeight: '300px' }}>
                {/* Current step content */}
                {currentStep === 0 && <PersonalInfoStep />}
                {currentStep === 1 && <ProfessionalInfoStep />}
                {currentStep === 2 && <AvailabilityStep />}
              </Box>
            )}
            
            {/* Navigation buttons */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              mt: 4, 
              pt: 2, 
              borderTop: '1px solid', 
              borderColor: 'divider' 
            }}>
              <Button 
                disabled={currentStep === 0 || submitting} 
                onClick={handleBack}
                variant="outlined"
              >
                Back
              </Button>
              
              {currentStep < steps.length - 1 ? (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={!isValid[Object.keys(isValid)[currentStep]] || submitting}
                >
                  Next
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={submitting || !isValid.availability}
                  color="primary"
                >
                  {submitting ? 'Submitting...' : 'Complete Registration'}
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default RegistrationForm;
